import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Edit3, Save, Loader2, ExternalLink, Wifi, Hand, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickUpdate = ({ sensors, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('auto'); // 'auto' or 'manual'
  const [levels, setLevels] = useState({});
  const [saving, setSaving] = useState(false);
  const [reading, setReading] = useState(false);
  const [modbusResults, setModbusResults] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const handleOpen = () => {
    // Initialize with current levels
    const currentLevels = {};
    sensors.forEach(s => {
      currentLevels[s.id] = s.current_level;
    });
    setLevels(currentLevels);
    setModbusResults(null);
    setOpen(true);
  };

  const handleReadModbus = async () => {
    setReading(true);
    setModbusResults(null);
    
    // First try direct Modbus (with 2 retries)
    let attempts = 0;
    let lastError = null;
    
    while (attempts < 2) {
      attempts++;
      try {
        const response = await axios.post(`${API}/sensors/read-modbus`, {}, {
          headers: getAuthHeaders(),
          timeout: 30000
        });
        
        if (response.data.success) {
          const newLevels = { ...levels };
          let successCount = 0;
          
          response.data.results.forEach(result => {
            if (result.success) {
              const sensor = sensors.find(s => s.id === result.sensor_id);
              if (sensor) {
                newLevels[sensor.id] = result.adjusted_value;
                successCount++;
              }
            }
          });
          
          setLevels(newLevels);
          setModbusResults(response.data.results);
          
          if (successCount === sensors.length) {
            toast.success(`✅ ${response.data.message}`);
          } else if (successCount > 0) {
            toast.warning(`⚠️ ${successCount}/${sensors.length} sensores leídos directamente`);
          }
          
          // If some sensors failed, try proxy for those
          if (successCount < sensors.length) {
            toast.info("Intentando proxy para sensores restantes...");
            await tryProxySync(newLevels, successCount);
          }
          
          setReading(false);
          return;
        } else {
          lastError = "Error en la respuesta del servidor";
        }
      } catch (error) {
        console.error(`Modbus read attempt ${attempts} failed:`, error);
        lastError = error.message || "Error de conexión";
        
        if (attempts < 2) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
    }
    
    // Direct Modbus failed, try proxy
    toast.info("Modbus directo falló, intentando proxy...");
    await tryProxySync({}, 0);
    setReading(false);
  };

  const tryProxySync = async (currentLevels, alreadySuccess) => {
    try {
      const response = await axios.post(`${API}/sensors/sync-from-proxy`, {}, {
        headers: getAuthHeaders(),
        timeout: 30000
      });
      
      if (response.data.success && response.data.sensors) {
        const newLevels = { ...currentLevels };
        
        response.data.sensors.forEach(result => {
          const sensor = sensors.find(s => s.name === result.name);
          if (sensor) {
            newLevels[sensor.id] = result.level;
          }
        });
        
        setLevels(newLevels);
        
        const proxyCount = response.data.sensors.length;
        const totalSuccess = alreadySuccess + proxyCount;
        
        if (totalSuccess > 0) {
          toast.success(`✅ ${totalSuccess} sensores actualizados (${proxyCount} via proxy)`);
          onUpdate && onUpdate();
        }
      } else {
        toast.error("❌ Error al sincronizar desde proxy");
      }
    } catch (error) {
      console.error("Proxy sync failed:", error);
      toast.error(`❌ Error proxy: ${error.message}`);
    }
  };

  const handleReadModbusSingle = async (sensorId) => {
    // Read single sensor for retry
    try {
      const response = await axios.post(`${API}/sensors/read-modbus`, {}, {
        headers: getAuthHeaders(),
        timeout: 15000
      });
      
      if (response.data.success) {
        const result = response.data.results.find(r => r.sensor_id === sensorId);
        if (result && result.success) {
          setLevels(prev => ({
            ...prev,
            [sensorId]: result.adjusted_value
          }));
          setModbusResults(prev => 
            prev ? prev.map(r => r.sensor_id === sensorId ? result : r) : [result]
          );
          toast.success(`✅ ${result.sensor_name}: ${result.adjusted_value.toFixed(2)} m`);
        }
      }
    } catch (error) {
      toast.error("Error al reintentar lectura");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const sensor of sensors) {
        const newLevel = parseFloat(levels[sensor.id]);
        if (!isNaN(newLevel) && newLevel !== sensor.current_level) {
          await axios.post(`${API}/sensors/${sensor.id}/reading`, {
            level: newLevel
          }, {
            headers: getAuthHeaders()
          });
        }
      }
      toast.success("Niveles actualizados");
      onUpdate();
      setOpen(false);
    } catch (error) {
      toast.error("Error al actualizar");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const openWebView = () => {
    window.open('https://www.weincloud.net/webview', '_blank');
  };

  const openWebVNC = () => {
    window.open('https://www.weincloud.net/webvnc', '_blank');
  };

  const getModbusStatus = (sensorId) => {
    if (!modbusResults) return null;
    const sensor = sensors.find(s => s.id === sensorId);
    if (!sensor) return null;
    const result = modbusResults.find(r => r.sensor_id === sensorId);
    return result;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleOpen}
          className="bg-blue-600/20 border-blue-500/30 text-blue-400 hover:bg-blue-600/30 hover:text-blue-300 h-8 px-3 text-xs"
          data-testid="quick-update-btn"
        >
          <Edit3 className="w-4 h-4 mr-1.5" />
          Actualizar Niveles
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wider text-zinc-100 text-sm">
            Actualizar Niveles de Agua
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          {/* Mode Toggle */}
          <div className="flex gap-2 p-1 bg-zinc-800 rounded-lg">
            <button
              onClick={() => setMode('auto')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                mode === 'auto' 
                  ? 'bg-blue-600 text-white shadow-lg' 
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              <Wifi className="w-4 h-4" />
              Automático
            </button>
            <button
              onClick={() => setMode('manual')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                mode === 'manual' 
                  ? 'bg-amber-600 text-white shadow-lg' 
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              <Hand className="w-4 h-4" />
              Manual
            </button>
          </div>

          {/* Auto Mode - Modbus */}
          {mode === 'auto' && (
            <div className="space-y-3">
              <div className="bg-blue-950/30 border border-blue-900/50 rounded-lg p-3">
                <p className="text-xs text-blue-300 mb-3">
                  Lee los valores directamente desde los PLCs vía Modbus TCP
                </p>
                <Button
                  onClick={handleReadModbus}
                  disabled={reading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white mb-2"
                >
                  {reading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Leyendo Modbus...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Leer Valores Modbus
                    </>
                  )}
                </Button>
                
                {/* Direct Proxy Sync Button */}
                <Button
                  onClick={async () => {
                    setReading(true);
                    await tryProxySync({}, 0);
                    setReading(false);
                  }}
                  disabled={reading}
                  variant="outline"
                  className="w-full border-green-600 text-green-400 hover:bg-green-600/20"
                >
                  {reading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sincronizando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Sincronizar desde Proxy (3 sensores)
                    </>
                  )}
                </Button>
              </div>

              {/* Modbus Results */}
              {modbusResults && (
                <div className="space-y-2">
                  {sensors.map(sensor => {
                    const result = getModbusStatus(sensor.id);
                    return (
                      <div key={sensor.id} className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-2">
                        <div className="flex items-center gap-2">
                          {result?.success ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-400" />
                          )}
                          <span className="text-zinc-300 text-sm font-mono">{sensor.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`font-mono font-bold ${result?.success ? 'text-zinc-100' : 'text-red-400'}`}>
                            {result?.success ? `${result.adjusted_value.toFixed(2)} m` : 'Error'}
                          </span>
                          {!result?.success && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-amber-400 hover:text-amber-300"
                              onClick={() => handleReadModbusSingle(sensor.id)}
                            >
                              <RefreshCw className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Manual Mode */}
          {mode === 'manual' && (
            <div className="space-y-3">
              <div className="bg-amber-950/30 border border-amber-900/50 rounded-lg p-3">
                <p className="text-xs text-amber-300 mb-2">
                  Introduce los valores manualmente consultando los HMI
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={openWebView}
                    className="flex-1 bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700 text-xs"
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    WebView
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={openWebVNC}
                    className="flex-1 bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700 text-xs"
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    WebVNC
                  </Button>
                </div>
                <p className="text-[10px] text-zinc-500 mt-2 text-center">
                  Contraseñas: 1234 / 111111 / ajservisistemas
                </p>
              </div>

              {/* Manual Input Fields */}
              <div className="space-y-3">
                {sensors.map(sensor => (
                  <div key={sensor.id} className="flex items-center gap-3">
                    <Label className="text-zinc-400 text-xs font-mono w-24 flex-shrink-0">
                      {sensor.name}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={levels[sensor.id] || ''}
                      onChange={(e) => setLevels(prev => ({
                        ...prev,
                        [sensor.id]: e.target.value
                      }))}
                      className="bg-zinc-800 border-zinc-700 text-zinc-100 font-mono h-9"
                      data-testid={`level-input-${sensor.id}`}
                    />
                    <span className="text-zinc-500 text-xs">{sensor.unit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800">
          <Button 
            variant="ghost" 
            onClick={() => setOpen(false)}
            className="text-zinc-400 h-9"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
            data-testid="save-levels-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Guardar Niveles
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickUpdate;
